import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsive } from './useResponsive';
import { optimizeImageUrl } from './imageUrl';

// Fixed DD/MM/YYYY format for article card dates, independent of locale.
const cardDateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

type ArticleCardProps = {
  title: string;
  slug: string;
  excerpt?: string;
  category?: string;
  readTime?: number;
  publishedAt?: string;
  coverImage?: string;
  videoUrl?: string;
  authors?: string[];
  authorName?: string;
  authorSlug?: string;
  locale?: string;
  href?: string;
  /** Load the cover image eagerly (use for the first card above the fold). */
  eager?: boolean;
};

function getYoutubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

/**
 * Responsive article card with video preview on hover.
 */
export function ArticleCard({
  title,
  excerpt,
  category,
  readTime,
  publishedAt,
  coverImage,
  videoUrl,
  authorName,
  authorSlug,
  locale = 'en',
  href = '#',
  eager = false,
}: ArticleCardProps) {
  const { t } = useTranslation('article');
  const { isMobile } = useResponsive();
  const isRtl = locale === 'ar';
  const [hovered, setHovered] = useState(false);
  const ytId = videoUrl ? getYoutubeId(videoUrl) : null;
  const showVideo = hovered && !!ytId;
  const cleanCover = optimizeImageUrl(coverImage, { width: 800, height: 500, quality: 80 });

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-md bg-white shadow-sm transition-shadow duration-200 hover:shadow-md ${isMobile ? 'flex-col' : 'sm:flex-row'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Cover image or video preview */}
      {(coverImage || ytId) && (
        <a
          href={href}
          className={`block overflow-hidden shrink-0 aspect-[16/10] sm:w-48 md:w-56 lg:w-64`}
        >
          {showVideo ? (
            <iframe
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&loop=1&playlist=${ytId}`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              className="h-full w-full border-0 pointer-events-none"
            />
          ) : coverImage ? (
            <img
              src={cleanCover}
              alt={title}
              width={800}
              height={500}
              loading={eager ? 'eager' : 'lazy'}
              fetchPriority={eager ? 'high' : undefined}
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : null}
        </a>
      )}
      {ytId && !showVideo && (
        <span className="absolute top-2 left-2 rounded px-1.5 py-0.5 bg-ink/80 text-white font-ui text-ui-xs flex items-center gap-1 z-10">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          Video
        </span>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col justify-between p-4 sm:p-5" dir={isRtl ? 'rtl' : undefined}>
        <div>
          {/* Category badge */}
          {category && (
            <span className="mb-2 inline-block rounded-pill bg-highlight/30 px-2.5 py-0.5 font-ui text-ui-xs font-medium text-ink">
              {category}
            </span>
          )}

          {/* Title */}
          <a href={href} className="block">
            <h3
              className={`
                font-editorial-body font-semibold text-ink
                text-h3-sm sm:text-h3-md lg:text-h3-lg
                leading-snug transition-colors group-hover:text-primary
                line-clamp-3
              `.trim().replace(/\s+/g, ' ')}
              lang={locale}
            >
              {title}
            </h3>
          </a>

          {/* Excerpt */}
          {excerpt && (
            <p className="mt-2 line-clamp-2 font-editorial-body text-body-sm text-ink-light">
              {excerpt}
            </p>
          )}
        </div>

        {/* Meta row */}
        <div
          className={`
            mt-3 flex flex-wrap items-center gap-x-3 gap-y-1
            font-ui text-ui-xs text-ink-light
            ${isRtl ? 'flex-row-reverse' : ''}
          `.trim().replace(/\s+/g, ' ')}
        >
          {authorName && (
            <>
              {authorSlug ? (
                <a
                  href={`/${locale}/author/${authorSlug}`}
                  className="font-medium text-secondary-700 hover:text-secondary-900 transition-colors truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {authorName}
                </a>
              ) : (
                <span className="truncate">{authorName}</span>
              )}
              {(readTime || publishedAt) && <span aria-hidden="true">·</span>}
            </>
          )}
          {readTime && (
            <span>{t('meta.readTime', { count: readTime })}</span>
          )}
          {publishedAt && (
            <time dateTime={publishedAt}>
              {cardDateFormatter.format(new Date(publishedAt))}
            </time>
          )}
        </div>
      </div>
    </article>
  );
}
